import type { FlightsPriceFreezeTrackingAttributes } from "./types";

export const FlightsPriceFreezePurchaseTrackingEvents = {
  PRICE_FREEZE_VIEWED_CTA: "price_freeze_viewed_cta",
  PRICE_FREEZE_ENTRY_SELECTED: "price_freeze_entry_selected",
  PRICE_FREEZE_VIEWED_MISSION_CONTROL: "price_freeze_viewed_mission_control",
  PRICE_FREEZE_DURATION_CHANGED: "price_freeze_duration_changed",
  PRICE_FREEZE_VIEWED_PAYMENT_SELECTION:
    "price_freeze_viewed_payment_selection",
  PRICE_FREEZE_PURCHASE_SELECTED: "price_freeze_purchase_selected",
  PRICE_FREEZE_VIEWED_CONFIRMATION_SCREEN:
    "price_freeze_viewed_confirmation_screen",
} as const;

export const FlightsPriceFreezeExerciseTrackingEvents = {
  PRICE_FREEZE_SELECTED_EXERCISE_ENTRY_POINT:
    "price_freeze_selected_exercise_entry_point",
  PRICE_FREEZE_VIEWED_EXERCISE_MISSION_CONTROL:
    "price_freeze_viewed_exercise_mission_control",
  PRICE_FREEZE_SELECTED_EXERCISE_CONTINUE:
    "price_freeze_selected_exercise_continue",
  PRICE_FREEZE_NO_AVAILABILITY: "price_freeze_no_availability",
  PRICE_FREEZE_REQUEST_REFUND: "price_freeze_request_refund",
} as const;

export const FLIGHTS_PRICE_FREEZE_PRODUCT_TYPE = "air-price-freeze" as const;

export type FlightsPriceFreezeHTSEventBasicProperties = {
  product_type: typeof FLIGHTS_PRICE_FREEZE_PRODUCT_TYPE;
};

export type FlightsPriceFreezeHTSEventProperties<Properties = unknown> =
  FlightsPriceFreezeHTSEventBasicProperties & Properties;

export type FlightsPriceFreezePurchaseEventName =
  typeof FlightsPriceFreezePurchaseTrackingEvents;
export type FlightsPriceFreezeExerciseEventName =
  typeof FlightsPriceFreezeExerciseTrackingEvents;

export const FlightsPriceFreezeEventName = {
  ...FlightsPriceFreezePurchaseTrackingEvents,
  ...FlightsPriceFreezeExerciseTrackingEvents,
} as const;

// Purchase events
type FlightsPriceFreezeEntryLoaded = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_VIEWED_CTA"];
  properties: FlightsPriceFreezeHTSEventProperties;
};

type FlightsPriceFreezeEntryClicked = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_ENTRY_SELECTED"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezeDetailsLoaded = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_VIEWED_MISSION_CONTROL"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezeDurationChanged = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_DURATION_CHANGED"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezePaymentLoaded = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_VIEWED_PAYMENT_SELECTION"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezePurchaseClicked = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_PURCHASE_SELECTED"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};
type FlightsPriceFreezePurchaseConfirmation = {
  name: FlightsPriceFreezePurchaseEventName["PRICE_FREEZE_VIEWED_CONFIRMATION_SCREEN"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezePurchaseHTSEvent =
  | FlightsPriceFreezeEntryLoaded
  | FlightsPriceFreezeEntryClicked
  | FlightsPriceFreezeDetailsLoaded
  | FlightsPriceFreezeDurationChanged
  | FlightsPriceFreezePaymentLoaded
  | FlightsPriceFreezePurchaseClicked
  | FlightsPriceFreezePurchaseConfirmation;

// Exercise events
type FlightsPriceFreezeExerciseEntryClicked = {
  name: FlightsPriceFreezeExerciseEventName["PRICE_FREEZE_SELECTED_EXERCISE_ENTRY_POINT"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezeExerciseMissionControlLoaded = {
  name: FlightsPriceFreezeExerciseEventName["PRICE_FREEZE_VIEWED_EXERCISE_MISSION_CONTROL"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};
type FlightsPriceFreezeExerciseContinueClicked = {
  name: FlightsPriceFreezeExerciseEventName["PRICE_FREEZE_SELECTED_EXERCISE_CONTINUE"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezeExerciseNoAvailability = {
  name: FlightsPriceFreezeExerciseEventName["PRICE_FREEZE_NO_AVAILABILITY"];
  properties: FlightsPriceFreezeHTSEventProperties<FlightsPriceFreezeTrackingAttributes>;
};

type FlightsPriceFreezeExerciseRequestRefund = {
  name: FlightsPriceFreezeExerciseEventName["PRICE_FREEZE_REQUEST_REFUND"];
  properties: FlightsPriceFreezeHTSEventProperties<{
    price_freeze_refund: boolean;
  }>;
};

type FlightsPriceFreezeExerciseHTSEvent =
  | FlightsPriceFreezeExerciseEntryClicked
  | FlightsPriceFreezeExerciseMissionControlLoaded
  | FlightsPriceFreezeExerciseContinueClicked
  | FlightsPriceFreezeExerciseNoAvailability
  | FlightsPriceFreezeExerciseRequestRefund;

export type FlightsPriceFreezeHTSEvent =
  | FlightsPriceFreezePurchaseHTSEvent
  | FlightsPriceFreezeExerciseHTSEvent;
